import { supabase } from "@/shared/integrations/supabase/client";
import {
  buildPropertySearchFilterForToken,
  getPropertySearchGroups,
  getPropertySearchTokens,
} from "@/features/properties/services/propertySearch";
import type { Property } from "@/shared/types/client";
import {
  buildDeliverableConstraints, DEFAULT_SORT, EMPTY_FILTERS, SORT_COLUMN_DB,
  type PropertyFilters, type PropertySort,
} from "@/features/properties/services/propertyFilters";
import { STATUS_FILTERS, DELIVERABLES, type StatusFilter, type DeliverableKey } from "@/features/properties/services/deliveryStatusConfig";
import { isAdminUser } from "@/shared/lib/adminUids";
import { getUserTeamMembership } from "@/shared/lib/userTeams";
import { firebaseAuth } from "@/shared/integrations/firebase/client";
import { isDemoUser } from "@/shared/lib/userIdUtils";

/**
 * How to scope a properties query to the current client.
 * - Super-admins (hardcoded UIDs), org owners & autoSyncTeams users: the whole
 *   client by organization_id.
 * - Other client users: their org AND only their teams' properties — we
 *   inner-join property_teams and filter on team_id. The filter carries a handful
 *   of team ids, never an ~org-sized id list (which overflows the request URL).
 *
 * organization_id is ALWAYS applied: it is the authoritative client link and it
 * cleanly excludes unsynced duplicate rows that have a null organization_id.
 *
 * Resolved async, then applied synchronously — never await the query object
 * itself (Supabase query builders are thenable, so awaiting one executes it).
 */
type OrgScope = { orgId: string; teamIds?: string[] };

/** Map Firebase team ids to Supabase team ids (teams.ref_id -> teams.id). */
async function mapToSupabaseTeamIds(teamRefIds: string[]): Promise<string[]> {
  if (teamRefIds.length === 0) return [];
  // teams isn't in the generated Supabase types yet; cast (same convention as
  // property_teams elsewhere in this file).
  const { data, error } = await (supabase as any)
    .from("teams")
    .select("id")
    .in("ref_id", teamRefIds);
  if (error) throw error;
  return (data ?? []).map((t: any) => t.id as string);
}

async function resolveOrgScopeUncached(projectId: string): Promise<OrgScope> {
  // Admins and the (read-only) demo account see the whole org. The demo has no
  // team membership, so the team-scoped path would inner-join on an empty list
  // and return nothing.
  if (isAdminUser(firebaseAuth?.currentUser?.uid) || isDemoUser()) {
    return { orgId: projectId };
  }
  const membership = await getUserTeamMembership();
  // Org owners & autoSyncTeams users see the whole org.
  if (membership.autoSyncTeams || membership.isOrgOwner) {
    return { orgId: projectId };
  }
  // Regular members: their org AND only their teams' properties.
  const teamIds = await mapToSupabaseTeamIds(membership.teamRefIds);
  return { orgId: projectId, teamIds };
}

// The properties page fans out ~6 data fetches in parallel, each of which calls
// resolveOrgScope. For a regular team member that re-runs the teams(ref_id)
// lookup every time — Sentry flagged it as an N+1 (6 identical requests per
// load). Memoize the resolved scope per (uid, projectId) for a short window so
// the burst collapses to a single lookup. The TTL bounds staleness if team
// membership changes mid-session; keying on uid prevents cross-user reuse.
const SCOPE_TTL_MS = 30_000;
const scopeCache = new Map<string, { at: number; scope: Promise<OrgScope> }>();

function resolveOrgScope(projectId: string): Promise<OrgScope> {
  const key = `${firebaseAuth?.currentUser?.uid ?? "anon"}:${projectId}`;
  const hit = scopeCache.get(key);
  if (hit && Date.now() - hit.at < SCOPE_TTL_MS) return hit.scope;

  const scope = resolveOrgScopeUncached(projectId);
  scopeCache.set(key, { at: Date.now(), scope });
  // Never cache a rejection — a transient failure must not poison the window.
  scope.catch(() => {
    if (scopeCache.get(key)?.scope === scope) scopeCache.delete(key);
  });
  return scope;
}

/**
 * Build a properties query for the resolved scope. organization_id is always
 * filtered; when teamIds is present we additionally inner-join property_teams
 * and filter by team_id (a small list) to restrict a member to their teams.
 *
 * `select`/`opts` mirror supabase's `.select()` args; the inner-join embed is
 * appended only for the team scope. Returns an untyped builder because the
 * property_teams embed isn't in the generated types.
 */
function scopedPropertiesQuery(
  scope: OrgScope,
  select: string,
  opts?: { count?: "exact"; head?: boolean },
): any {
  const hasTeams = scope.teamIds !== undefined;
  const finalSelect = hasTeams
    ? `${select}, property_teams!inner(team_id)`
    : select;
  let q: any = (supabase as any)
    .from("properties")
    .select(finalSelect, opts)
    .eq("organization_id", scope.orgId);
  if (hasTeams) q = q.in("property_teams.team_id", scope.teamIds);
  return q;
}

/**
 * Resolve the property ids linked to any of the given teams. Returns null when no
 * teams are selected (filter inactive). Resolved async, then applied synchronously
 * in applyFilters — never await the query builder during resolution.
 */
async function resolveTeamPropertyIds(teamIds: string[]): Promise<string[] | null> {
  if (teamIds.length === 0) return null;
  const { data, error } = await (supabase as any)
    .from("property_teams")
    .select("property_id")
    .in("team_id", teamIds);
  if (error) throw error;
  const uniqueIds = new Set((data ?? []).map((r: any) => r.property_id as string));
  return Array.from(uniqueIds) as string[];
}

/** Apply stage + deliverable filters to a properties query via the builder chain. */
function applyFilters<T>(query: T, filters: PropertyFilters, teamPropertyIds: string[] | null): T {
  let q: any = query;

  // Status is tri-state per stage: 'has' includes that status (OR via .in),
  // 'missing' excludes it (AND of .neq).
  const stageEntries = Object.entries(filters.stages) as [StatusFilter, "any" | "has" | "missing"][];
  const includeStages = stageEntries.filter(([, s]) => s === "has").map(([k]) => k);
  const excludeStages = stageEntries.filter(([, s]) => s === "missing").map(([k]) => k);
  if (includeStages.length > 0) q = q.in("delivery_status", includeStages);
  for (const stage of excludeStages) q = q.neq("delivery_status", stage);

  for (const c of buildDeliverableConstraints(filters.deliverables)) {
    q = q.eq(c.column, c.value);
  }
  if (teamPropertyIds !== null) {
    q = q.in("id", teamPropertyIds);
  }
  return q as T;
}

export type GroupedPropertiesResult = {
  label: string;
  count: number;
  properties: Property[];
};

/**
 * Get total count of properties for a project (client).
 */
export async function getPropertiesCount(projectId: string, searchTerm = "", filters: PropertyFilters = EMPTY_FILTERS): Promise<number> {
  const scope = await resolveOrgScope(projectId);
  const teamPropertyIds = await resolveTeamPropertyIds(filters.teamIds);
  let query = scopedPropertiesQuery(scope, "*", { count: "exact", head: true });

  for (const token of getPropertySearchTokens(searchTerm)) {
    query = query.or(buildPropertySearchFilterForToken(token));
  }

  query = applyFilters(query, filters, teamPropertyIds);

  const { count } = await query;
  return count ?? 0;
}

/**
 * Get a page of properties with asset counts, ordered by created_at desc.
 * Returns properties transformed to the Property type used by the client UI.
 */
export async function getPropertiesPage(
  projectId: string,
  from: number,
  to: number,
  searchTerm = "",
  filters: PropertyFilters = EMPTY_FILTERS,
  sort: PropertySort = DEFAULT_SORT
): Promise<Property[]> {
  const scope = await resolveOrgScope(projectId);
  const teamPropertyIds = await resolveTeamPropertyIds(filters.teamIds);
  let query = scopedPropertiesQuery(scope, `*, property_assets(count)`);

  for (const token of getPropertySearchTokens(searchTerm)) {
    query = query.or(buildPropertySearchFilterForToken(token));
  }

  query = applyFilters(query, filters, teamPropertyIds);

  const { data, error } = await query
    .order(SORT_COLUMN_DB[sort.column], { ascending: sort.ascending })
    .range(from, to);

  if (error) throw error;

  const propertyIds = (data ?? []).map((p: { id: string }) => p.id);

  // Fetch latest survey date per property
  const surveyDateMap: Record<string, string> = {};
  if (propertyIds.length > 0) {
    const { data: surveys } = await supabase
      .from("property_attributes")
      .select("property_id, captured_at")
      .in("property_id", propertyIds)
      .order("captured_at", { ascending: false });

    if (surveys) {
      for (const s of surveys as { property_id: string; captured_at: string }[]) {
        // Keep only the first (latest) per property
        if (!surveyDateMap[s.property_id]) {
          surveyDateMap[s.property_id] = s.captured_at;
        }
      }
    }
  }

  // Fetch initiatives (teams) per property. teams / property_teams aren't in the
  // generated Supabase types yet; cast to untyped (same convention as elsewhere).
  const teamsMap: Record<string, { id: string; name: string }[]> = {};
  if (propertyIds.length > 0) {
    const { data: links } = await (supabase as any)
      .from("property_teams")
      .select("property_id, teams(id, name)")
      .in("property_id", propertyIds);

    if (links) {
      for (const link of links as {
        property_id: string;
        teams: { id: string; name: string } | null;
      }[]) {
        if (!link.teams) continue;
        (teamsMap[link.property_id] ??= []).push({
          id: link.teams.id,
          name: link.teams.name,
        });
      }
    }
  }

  const transformedProperties: Property[] = ((data ?? []) as any[]).map((prop: any) => ({
    ...prop, // keep all raw DB columns (delivery_status, deliverables, etc.)
    id: prop.id,
    name: prop.name,
    address: prop.address,
    status: "active" as const,
    project_id: prop.project_id,
    assetCount: prop.property_assets?.[0]?.count ?? 0,
    completionPercentage: 0,
    lastUpdated: prop.updated_at,
    lastSurveyDate: surveyDateMap[prop.id] ?? null,
    teams: teamsMap[prop.id] ?? [],
  }));

  return transformedProperties;
}

export async function getGroupedPropertiesPage(
  projectId: string,
  searchTerm: string,
  pageSize: number
): Promise<GroupedPropertiesResult[]> {
  const groups = getPropertySearchGroups(searchTerm);

  return Promise.all(
    groups.map(async (group) => {
      const [count, properties] = await Promise.all([
        getPropertiesCount(projectId, group.label),
        getPropertiesPage(projectId, 0, pageSize - 1, group.label),
      ]);

      return {
        label: group.label,
        count,
        properties,
      };
    })
  );
}

/**
 * Static per-stage totals for the filter chips. Three cheap head counts in parallel.
 * Reflects the current search term but, by design, IGNORES active stage/deliverable
 * filters — the chips show full per-stage totals, not counts narrowed by other filters.
 */
export async function getStageCounts(projectId: string, searchTerm = ""): Promise<Record<StatusFilter, number>> {
  const scope = await resolveOrgScope(projectId);
  const entries = await Promise.all(
    STATUS_FILTERS.map(async (stage) => {
      let query = scopedPropertiesQuery(scope, "*", { count: "exact", head: true });
      query = query.eq("delivery_status", stage);
      for (const token of getPropertySearchTokens(searchTerm)) {
        query = query.or(buildPropertySearchFilterForToken(token));
      }
      const { count } = await query;
      return [stage, count ?? 0] as const;
    })
  );
  return Object.fromEntries(entries) as Record<StatusFilter, number>;
}

export interface PropertyTeam {
  id: string;
  name: string;
  color?: string;
}

/** Teams that appear on this client's properties (via property_teams → teams). */
export async function getTeams(projectId: string): Promise<PropertyTeam[]> {
  const scope = await resolveOrgScope(projectId);
  const { data: propertyRows, error: propError } = await scopedPropertiesQuery(
    scope,
    "id",
  );
  if (propError) throw propError;
  const propertyIds = (propertyRows ?? []).map((p: any) => p.id as string);
  if (propertyIds.length === 0) return [];

  // teams / property_teams aren't in the generated Supabase types yet; cast
  // to untyped (same convention as projectsApi.ts / mergeSurveys.ts).
  const { data: linkRows, error: linkError } = await (supabase as any)
    .from("property_teams")
    .select("team_id")
    .in("property_id", propertyIds);
  if (linkError) throw linkError;
  const teamIds = [...new Set((linkRows ?? []).map((r: any) => r.team_id as string))];
  if (teamIds.length === 0) return [];

  const { data: teamRows, error: teamError } = await (supabase as any)
    .from("teams")
    .select("id, name")
    .in("id", teamIds)
    .order("name", { ascending: true });
  if (teamError) throw teamError;

  return (teamRows ?? []).map((t: any) => ({
    id: t.id as string,
    name: (t.name as string) ?? "",
  }));
}

export type PortfolioProgress = {
  total: number;
  stages: Record<StatusFilter, number>;
  deliverables: Record<DeliverableKey, number>;
};

/**
 * Whole-portfolio progress for the hero band: stage distribution + deliverable
 * completion counts for the entire client. Deliberately IGNORES search and the
 * active filters — it is the stable denominator; the table below reacts to filters.
 * Cheap parallel head counts, mirroring getStageCounts.
 */
export async function getPortfolioProgress(projectId: string): Promise<PortfolioProgress> {
  const scope = await resolveOrgScope(projectId);
  const headCount = async (apply: (q: any) => any): Promise<number> => {
    const q = apply(scopedPropertiesQuery(scope, "*", { count: "exact", head: true }));
    const { count } = await q;
    return count ?? 0;
  };

  const [total, stageEntries, deliverableEntries] = await Promise.all([
    headCount((q) => q),
    Promise.all(
      STATUS_FILTERS.map(async (s) => [s, await headCount((q) => q.eq("delivery_status", s))] as const),
    ),
    Promise.all(
      DELIVERABLES.map(async (k) => [k, await headCount((q) => q.eq(k, true))] as const),
    ),
  ]);

  return {
    total,
    stages: Object.fromEntries(stageEntries) as Record<StatusFilter, number>,
    deliverables: Object.fromEntries(deliverableEntries) as Record<DeliverableKey, number>,
  };
}

